import { ImageTagViewModel, ProductPriceViewModel } from '~/lib/data-contract';
import { ProductTags, TextVariants, Typography } from '~/shared/components';
import { VisuallyHidden } from '../VisuallyHidden';
import { useTranslation } from '~/shared/utils';
import { ViabillPricetag } from '../viabill/viabill-pricetag.component';
import { useIsViabillEnabled } from '../viabill/viabill.config';
import styles from './price-group.module.css';
import clsx from 'clsx';

export type PriceGroupProps = ProductPriceViewModel & {
    variant?: TextVariants;
    imageTags?: ImageTagViewModel[];
    isPDP?: boolean;
    orientation?: 'horizontal' | 'vertical-bottom' | 'vertical-top';
};

export const PriceGroup = (props: PriceGroupProps) => {
    const {
        displaySalePrice,
        displayPrice,
        displayUnitPrice,
        isOnSale,
        variant = 'body',
        orientation = 'horizontal',
        imageTags,
        isPDP,
        isMemberDiscountedPrice,
        isSDMultiBuyDiscountedPrice,
        displayLowestPrice30Days,
        rawSalePrice,
        rawUnitPrice,
    } = props;

    // The SDMultiBuy campaign price is conditional on buying several items: it carries an asterisk,
    // and the normal price stays unstruck because a single item is still sold at that price.
    const isSDMultiBuy = !!isSDMultiBuyDiscountedPrice;

    const { translate } = useTranslation();
    const isViabillEnabled = useIsViabillEnabled();
    const viabillPrice = isOnSale ? rawSalePrice : rawUnitPrice;

    const orientationStyles = clsx({
        [styles.horizontal]: orientation === 'horizontal',
        [styles.verticalTop]: orientation === 'vertical-top',
        [styles.verticalBottom]: orientation === 'vertical-bottom',
    });

    return (
        <div>
            {isMemberDiscountedPrice && isOnSale && (
                <Typography
                    variant={variant === 'bodySm' ? 'caption' : 'bodyXs'}
                    as="div"
                    color="discount"
                    textAlign={orientation === 'horizontal' ? 'left' : 'right'}
                >
                    {translate('price.memberDiscountedLabel')}
                </Typography>
            )}
            <div>
                <div className={clsx(orientationStyles, styles.root)}>
                    {isOnSale ? (
                        <Typography
                            className={clsx(styles.discounted, 'price-selector-on-sale')}
                            variant={variant}
                            as="span"
                            color="discount"
                        >
                            {displaySalePrice}
                            {isSDMultiBuy && <sup>*</sup>}
                        </Typography>
                    ) : null}
                    <div
                        className={`${isOnSale && !isMemberDiscountedPrice && !isSDMultiBuy ? styles.original : ''}`}
                    >
                        <Typography variant={variant} as="span" className="price-selector">
                            <VisuallyHidden
                                children={translate(`price.${isOnSale && !isSDMultiBuy ? 'previous' : 'current'}`)}
                            />
                            {displayPrice || displayUnitPrice}
                        </Typography>
                    </div>
                </div>
                {displayLowestPrice30Days && (
                    <div className={styles.lowestPrice30DaysWrapper}>
                        <Typography variant={'caption'} as="span">
                            {translate('price.lowestPrice30Days')}
                        </Typography>
                        <Typography variant={'caption'} as="span">
                            {displayLowestPrice30Days}
                        </Typography>
                    </div>
                )}
                {(!!imageTags?.length) && (
                    <div className={styles.imageTagsWrapper}>
                        <ProductTags productTags={imageTags} isCampaign />
                    </div>
                )}
            </div>

            {isPDP && isViabillEnabled && viabillPrice !== undefined && (
                <div className={styles.viabillWrapper}>
                    <ViabillPricetag price={viabillPrice} />
                </div>
            )}
        </div>
    );
};
